import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../store/hooks";
import { capture_donation } from "../../../services/api/donation";
import Navbar from "../../../themes/header/nav_bar";
import FooterComponent from "../../../themes/footer";
import { CheckCircle, XCircle, Loader } from "lucide-react";

const DonationStatus: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const searchParams = new URLSearchParams(location.search);

  const token = searchParams.get("token");
  const orderID = searchParams.get("orderID");
  const captureId = token || orderID;

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (captureId) {
      capture_donation(dispatch, { orderID: captureId })
        .then(() => {
          setStatus("success");
        })
        .catch((error: any) => {
          console.error("Capture error:", error);
          setStatus("error");
          setErrorMessage(
            error?.response?.data?.message || "Failed to process donation."
          );
        });
    } else {
      // No token/orderID found, maybe show error or redirect
      setStatus("error");
      setErrorMessage("Invalid donation session.");
    }
  }, [captureId, dispatch]);

  return (
    <div className="min-h-screen flex flex-col font-abel bg-gray-50">
      <Navbar />
      <main className="flex-grow flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
          {status === "loading" && (
            <div className="flex flex-col items-center py-10">
              <Loader className="w-16 h-16 text-[#243677] animate-spin mb-4" />
              <h2 className="text-2xl font-semibold text-gray-800">
                Processing Donation...
              </h2>
              <p className="text-gray-500 mt-2">
                Please wait while we confirm your payment.
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="flex flex-col items-center py-6">
              <CheckCircle className="w-20 h-20 text-green-500 mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Thank You!
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Your donation has been successfully received. We appreciate your
                support.
              </p>
              <button
                onClick={() => navigate("/")}
                className="w-full bg-[#243677] text-white py-3 px-6 rounded-xl font-medium hover:bg-[#1a2b66] transition-colors"
              >
                Back to Home
              </button>
            </div>
          )}

          {status === "error" && (
            <div className="flex flex-col items-center py-6">
              <XCircle className="w-20 h-20 text-red-500 mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Donation Failed
              </h2>
              <p className="text-lg text-gray-600 mb-8">{errorMessage}</p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => navigate("/give")}
                  className="flex-1 border border-[#243677] text-[#243677] py-3 px-6 rounded-xl font-medium hover:bg-blue-50 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="flex-1 bg-[#243677] text-white py-3 px-6 rounded-xl font-medium hover:bg-[#1a2b66] transition-colors"
                >
                  Home
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <FooterComponent />
    </div>
  );
};

export default DonationStatus;
